/**
 * Currency-rate snapshot cache for the web client.
 *
 * The web client downloads the exchange rates once at startup (the expanded
 * bootstrap GraphQL query) and stores the snapshot here. Screens then do all currency conversion math locally from
 * this cache — no per-screen rate re-fetches.
 *
 * The module is a pure cache: no side effects at import time, and consumers
 * call the setters explicitly on startup.
 */

import { buildCurrencyMap } from './../shared-site-web/net-worth.js';
import {
	attachAccountEquivalentsWithMap,
	attachBaseEquivalentWithMap,
	computeRate,
	convertBetweenWithMap,
	convertToBaseWithMap,
	enrichCurrencyDisplayWithMap,
	rateFor,
	resolveBaseCurrency,
	resolveUsdToBase,
} from './currency-rates-helpers.js';

/** @type {{ currencies: Array<{ code: string, name: string, symbol: string, decimalPlaces: number, rate: number | null }>, baseCurrency: string | null, usdRates: Record<string, number> }} */
let snapshot = { currencies: [], baseCurrency: null, usdRates: {} };

export { computeRate, convertBetweenWithMap, resolveUsdToBase };
export { attachAccountEquivalentsWithMap, attachBaseEquivalentWithMap, enrichCurrencyDisplayWithMap };

/**
 * Attach the computed base-relative `rate` to each currency row.
 * @param {Array<Record<string, any>>} currencies
 * @param {Record<string, number>} [usdRates]
 * @param {string | null} [baseCurrency]
 * @returns {Array<Record<string, any>>}
 */
export function attachPublicRates(currencies, usdRates = snapshot.usdRates, baseCurrency = snapshot.baseCurrency) {
	return currencies.map((c) => ({
		...c,
		rate: rateFor(c, usdRates, baseCurrency),
	}));
}

/**
 * Store the public USD→* standard rates.
 * @param {Record<string, number>} usdRates
 * @returns {void}
 */
export function setPublicUsdRates(usdRates) {
	snapshot = { ...snapshot, usdRates };
}

/**
 * Fetch public standard exchange rates.
 * @param {string} baseUrl
 * @returns {Promise<{ base: string, date: string, rates: Record<string, number> } | null>}
 */
export async function fetchPublicRates(baseUrl) {
	try {
		const res = await fetch(`${baseUrl}/api/public/rates`, { headers: { Accept: 'application/json' } });
		if (!res.ok) {
			return null;
		}
		const data = await res.json();
		if (!data || typeof data.rates !== 'object' || data.rates === null) {
			return null;
		}
		return { base: String(data.base || 'USD'), date: String(data.date || ''), rates: data.rates };
	} catch {
		return null;
	}
}

/**
 * Replace the cached rate snapshot.
 * @param {Array<Record<string, any>>} currencies
 * @param {string | null} baseCurrency
 * @returns {void}
 */
export function setCurrencyRateSnapshot(currencies, baseCurrency) {
	snapshot = { ...snapshot, currencies: /** @type {Array<{ code: string, name: string, symbol: string, decimalPlaces: number, rate: number | null }>} */ (currencies), baseCurrency };
}

/**
 * @returns {{ currencies: Array<{ code: string, name: string, symbol: string, decimalPlaces: number, rate: number | null }>, baseCurrency: string | null, usdRates: Record<string, number> }}
 */
export function getCurrencyRateSnapshot() {
	return snapshot;
}

/**
 * Normalize a currency-list response into snapshot rows.
 * @param {Array<Record<string, any>>} currencies
 * @param {string | null} [baseCurrency]
 * @returns {void}
 */
export function syncCurrencySnapshot(currencies, baseCurrency) {
	const derivedBase = resolveBaseCurrency(currencies, baseCurrency, snapshot.baseCurrency);
	const normalized = currencies.map((c) => ({
		code: String(c.code ?? ''),
		name: c.name ?? c.code ?? '',
		symbol: c.symbol,
		decimalPlaces: Number(c.decimalPlaces),
		rate: rateFor(c, snapshot.usdRates, derivedBase),
	}));
	setCurrencyRateSnapshot(normalized, derivedBase);
}

/**
 * Update just the snapshot's base currency.
 * @param {string | null} baseCurrency
 * @returns {void}
 */
export function setSnapshotBaseCurrency(baseCurrency) {
	snapshot = { ...snapshot, baseCurrency };
}

/**
 * A currency -> { symbol, decimalPlaces, rate } map.
 * @returns {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>}
 */
export function getCurrencyRateMap() {
	return buildCurrencyMap(snapshot.currencies);
}

/**
 * Convert an amount into base currency using cached snapshot.
 * @param {number | null | undefined} amount
 * @param {string | null | undefined} code
 * @returns {number | null}
 */
export function convertToBase(amount, code) {
	return convertToBaseWithMap(amount, code, snapshot.baseCurrency, getCurrencyRateMap());
}

/**
 * Attach a converted `baseEquivalent` to each row.
 * @param {Array<Record<string, any>>} rows
 * @param {(row: Record<string, any>) => { amount: number | null | undefined, code: string | null | undefined }} accessor
 * @returns {Array<Record<string, any>>}
 */
export function attachBaseEquivalent(rows, accessor) {
	return attachBaseEquivalentWithMap(rows, accessor, snapshot.baseCurrency, getCurrencyRateMap());
}

/**
 * Fill in currency display metadata on entity rows.
 * @param {Array<Record<string, any>>} rows
 * @returns {Array<Record<string, any>>}
 */
export function enrichCurrencyDisplay(rows) {
	return enrichCurrencyDisplayWithMap(rows, getCurrencyRateMap());
}

/**
 * Attach converted balance/principal equivalents to account rows.
 * @param {Array<Record<string, any>>} accounts
 * @returns {Array<Record<string, any>>}
 */
export function attachAccountEquivalents(accounts) {
	return attachAccountEquivalentsWithMap(accounts, snapshot.baseCurrency, getCurrencyRateMap());
}
